import { MouseEventHandler, useEffect, useRef } from "react";
import type { TProgressProps } from "@/types";

const useProgress = (props: TProgressProps) => {
  const {
    isPlay,
    isLoop = true,
    getDuration,
    onChangeTime,
    onOutOfControl,
    onControl,
    onEnd,
  } = props;
  
  const rootEleRef = useRef<HTMLDivElement>(null);
  const controlBarEleRef = useRef<HTMLDivElement>(null);
  const controlButtonEleRef = useRef<any>(null);
  const progressEleRef = useRef<HTMLDivElement>(null);
  const progressRef = useRef(0);  // px
  const lastTimeRef = useRef(0);  // ms
  const currentTimeRef = useRef(0); // ms
  const isControlRef = useRef(false);
  const lastXRef = useRef(0);  // px
  const frameIdRef = useRef(0);

  const updateState = (progress: number, currentTime: number) => {    
    const duration = getDuration();
    if (duration > 0) {
      const total = rootEleRef.current!.clientWidth;
      progressRef.current = Math.min(Math.max(0, progress), total);
      currentTimeRef.current = Math.min(Math.max(0, currentTime), duration);
    }     
  }

  const applyEle = () => {
    const progressEle = progressEleRef.current;
    const controlEle = controlButtonEleRef.current;
    const progress = progressRef.current;
    if (progressEle) {
      progressEle.style.width = `${progress}px`;
    }
    if (controlEle) {
      controlEle.style.left = `${progress}px`;
    }
  }

  const back2start = () => {
    updateState(0, 0);
    applyEle();
  }

  const start = (time: number) => {    
    const duration = getDuration();        
    if (duration > 0) {      
      if (isControlRef.current) {
        return;
      }
      const total = rootEleRef.current!.clientWidth;
      const progress = progressRef.current;
      const lastTime = lastTimeRef.current;
      const currentTime = currentTimeRef.current;
      if (total > 0 && lastTime > 0) {
        const delta = time - lastTime;
        const perc = delta / duration;
        const move = total * perc;
        updateState(progress + move, currentTime + delta)
        applyEle();
      }      
      if (progress >= total) {
        back2start();
        onEnd?.(); 
      }
      if (progress < total || isLoop) {
        lastTimeRef.current = time;
        frameIdRef.current = requestAnimationFrame(start);
      }
    }
  }

  const play = () => {
    lastTimeRef.current = performance.now();
    frameIdRef.current = requestAnimationFrame(start);
  }

  const pause = () => {
    cancelAnimationFrame(frameIdRef.current);
  }

  const focusControlButton: MouseEventHandler<HTMLButtonElement> = (e) => {
    e.preventDefault();
    const { pageX } = e;
    isControlRef.current = true;
    lastXRef.current = pageX;
    onControl?.();
  }

  const moveControlButton = (e: MouseEvent) => {
    e.preventDefault();
    if (isControlRef.current) {
      const duration = getDuration();
      if (duration > 0) {
        const { pageX } = e;
        const total = rootEleRef.current!.clientWidth;
        const progress = progressRef.current;
        const currentTime = currentTimeRef.current;
        const lastX = lastXRef.current;
        const deltaX = pageX - lastX;
        const perc = deltaX / total;
        const deltaTime = duration * perc;
        updateState(progress + deltaX, currentTime + deltaTime);
        lastXRef.current = pageX;
        applyEle();
        onChangeTime?.(currentTimeRef.current);
      }
    }
  }

  const blurControlButton = (e: MouseEvent) => {
    e.preventDefault();
    if (isControlRef.current) {
      isControlRef.current = false;      
      onOutOfControl?.();
    }
  } 

  const clickControlBar: MouseEventHandler<HTMLDivElement> = (e) => {
    const duration = getDuration();
    if (duration > 0) {
      isControlRef.current = true;

      const { clientX } = e;
      const total = rootEleRef.current!.clientWidth;
      // 获取元素的边界框
      const rect = controlBarEleRef.current!.getBoundingClientRect();
      // 计算点击位置相对于元素左边缘的距离
      const offsetX = clientX - rect.left;
      const perc = offsetX / total;
      const time = perc * duration;
      updateState(offsetX, time);
      
      isControlRef.current = false;
      onChangeTime?.(time);
      applyEle();
    }
  }

  useEffect(() => {
    document.addEventListener("mousemove", moveControlButton);
    document.addEventListener("mouseup", blurControlButton);

    return () => {
      document.removeEventListener("mousemove", moveControlButton);
      document.removeEventListener("mouseup", blurControlButton);
    }
  }, []);

  useEffect(() => {
    isPlay ? play() : pause();
  }, [isPlay]);

  useEffect(() => {
    return () => {
      cancelAnimationFrame(frameIdRef.current);
    }
  }, []);

  return {
    rootEleRef,
    progressEleRef,
    controlBarEleRef,
    controlButtonEleRef,
    clickControlBar,
    focusControlButton,
  }
}

export default useProgress;